import { NgxUiLoaderService } from 'ngx-ui-loader';
import { AutocompleteService } from '../../services/autocomplete.service';
import {
    ISieveConf,
    ISieveFilterObject,
    ISieveSortsObject,
    SieveSortOrder,
    SieveString
} from '../sieve/sieveString.class';
import { LocalStorageService } from '../../services/local-storage.service';
import { OnInit } from '@angular/core';
import { PataviumRole } from '../../models/request-and-responses/rest-api/enums/role.enum.model';
import { EnergyOrGasContract } from '../../models/request-and-responses/rest-api/enums/energyOrGasContract.enum';
import { Observable } from 'rxjs/Observable';
import { AutoCompleteType } from './autoCompleteType';
import { IPageConfig } from '../../models/frontend/page-config';
import { LocalStorageKey } from '../../models/frontend/local-storage.key';

export interface IWithSearchBarAndSieve {
    rows: number;
    page: number;
    pageSize: number;
    filters: ISieveFilterObject[];
    sorts: ISieveSortsObject[];
    initData(): void;
    onPageChange(sieveConf: ISieveConf): void;
    onSearch(sieveConf: ISieveConf): void;
    onFilterSearch(object: string): void;
    sortBy(prop: string, order: number): void;
}

export abstract class WithSearchBarAndSieve implements IWithSearchBarAndSieve, OnInit {
    public page: number;
    public pageSize: number;
    public filters: ISieveFilterObject[];
    public sorts: ISieveSortsObject[];
    public localStorageKey: LocalStorageKey;
    private readonly _defaultPage: number;
    private readonly _defaultPageSize: number;
    private readonly _defaultSorts: ISieveSortsObject[];
    private readonly _defaultFilters: ISieveFilterObject[];
    protected AutoCompleteTypeChoose = AutoCompleteType;

    public rows: number;

    public searchprop: string;
    public searchval: string;

    //contructor
    protected constructor(
        defaultFilters: ISieveFilterObject[] = [],
        defaultSort: ISieveSortsObject[] = [],
        protected ptautocomplete: AutocompleteService,
        protected ngxService: NgxUiLoaderService,
        protected localStorageService: LocalStorageService,
        defaultPage: number = SieveString.DEAFULT_PAGE,
        defaultPageSize: number = SieveString.DEAFULT_PAGE_SIZE
    ) {
        this.page = defaultPage;
        this._defaultPage = defaultPage;

        this.pageSize = defaultPageSize;
        this._defaultPageSize = defaultPageSize;

        this.filters = defaultFilters;
        this._defaultFilters = [];
        for (const df of defaultFilters) {
            this._defaultFilters.push(df);
        }

        this.sorts = defaultSort;
        this._defaultSorts = [];
        for (const ds of defaultSort) {
            this._defaultSorts.push(ds);
        }
    }

    //abstact
    abstract initData(): void;

    ngOnInit() {
        this.ngxService.start();
        setTimeout(() => {
            this.ngxService.stop();
        }, 1000);
        setTimeout(() => {
            this.ngxService.stopAll();
        }, 5000);
    }

    get sieveConf(): ISieveConf {
        return {
            page: this.page,
            pageSize: this.pageSize,
            filters: this.filters,
            sorts: this.sorts
        };
    }

    protected customFilterConfiguration(pageConfig: IPageConfig): IPageConfig {
        return pageConfig;
    }

    updatePageConfig(): void {
        let pageConfig: IPageConfig = {
            pageSize: this.pageSize,
            sorts: this.sorts,
            filters: []
        };
        console.log(`Contract page configuration`, pageConfig);
        pageConfig = this.customFilterConfiguration(pageConfig);
        this.localStorageService.setItem(this.localStorageKey, pageConfig);
    }

    onPageChange(sieveConf: ISieveConf) {
        //keep filters unchanged
        this.page = sieveConf.page;
        this.pageSize = sieveConf.pageSize;
        this.updatePageConfig();
        this.initData();
    }

    onSearch(sieveConf: ISieveConf) {

        this.page = 1;

        this.searchprop = null;
        this.searchval = null;
        this.filters = [];
        //copy
        for (const df of this._defaultFilters) {
            this.filters.push(df);
        }
        for (const _filter of sieveConf.filters) {
            this.filters.push(<ISieveFilterObject>_filter);
        }
        this.sorts = this._defaultSorts;
        this.initData();
    }

    onFilterSearch(object: string) {

        try {

            const obj = JSON.parse(object);
            this.searchprop = obj.prop;
            this.searchval = obj.val;
            this.initData();

        } catch (e) {}

    }

    sortBy(
        prop: any /*should be string but actual implementation make the ide to alert the owner about param type*/,
        order: number
    ) {
        const _sort = prop;
        let _order = SieveSortOrder.Asc;
        if (order === -1) {
            _order = SieveSortOrder.Desc;
        }
        this.sorts = [{ key: _sort, order: _order }];
        this.updatePageConfig();
        this.initData();
    }

    protected loadConfiguration(): IPageConfig {
        if (this.localStorageKey) {
            let pageConfig: IPageConfig = <IPageConfig>(
                this.localStorageService.getItem(this.localStorageKey)
            );
            let time = 1000;
            const delay = 200;
            //TODO: probably you can set this into afterInit.
            while (pageConfig == null && time > 0) {
                setTimeout(null, delay);
                pageConfig = <IPageConfig>this.localStorageService.getItem(this.localStorageKey);
                time -= delay;
            }
            if (pageConfig == null) {
                pageConfig = { sorts: null, filters: null, pageSize: null };
            }
            if (pageConfig.pageSize != null) {
                this.pageSize = pageConfig.pageSize;
            }
            if (pageConfig.sorts != null) {
                this.sorts = pageConfig.sorts;
            }
            return pageConfig;
        }
        return null;
    }

    public autocompleteFactory(
        autoCompleteType: AutoCompleteType,
        energyOrGas?: EnergyOrGasContract
    ): (text: Observable<string>) => Observable<string[]> {
        switch (autoCompleteType) {
            case AutoCompleteType.City:
                return (userInput: Observable<string>) => {
                    return this.ptautocomplete.apiCities(userInput);
                };
            case AutoCompleteType.Province:
                return (userInput: Observable<string>) => {
                    return this.ptautocomplete.apiProvinces(userInput);
                };
            case AutoCompleteType.Supplier:
                return (userInput: Observable<string>) => {
                    return this.ptautocomplete.apiSupplier(userInput);
                };
            case AutoCompleteType.Distributor:
                return (userInput: Observable<string>) => {
                    switch (energyOrGas) {
                        case EnergyOrGasContract.Energy:
                            return this.ptautocomplete.apiDistributorEnergy(userInput);
                        case EnergyOrGasContract.Gas:
                            return this.ptautocomplete.apiDistributorGas(userInput);
                    }
                };
            default:
                return;
        }
    }

    getSingleRoleName(r: PataviumRole) {
        switch (r) {
            case PataviumRole.Agency:
                return 'team';
            case PataviumRole.BackOfficeOperator:
            case PataviumRole.AgencyOperator:
                return 'operatore';
            case PataviumRole.Agent:
                return 'venditore';
            case PataviumRole.Admin:
                return 'amministratore';
            case PataviumRole.SpecializedOperator:
                return 'operatore specializzato';
        }
    }

    getPluralRoleName(r: PataviumRole) {
        switch (r) {
            case PataviumRole.Agency:
                return 'team';
            case PataviumRole.BackOfficeOperator:
            case PataviumRole.AgencyOperator:
                return 'operatori';
            case PataviumRole.Agent:
                return 'venditori';
            case PataviumRole.Admin:
                return 'amministratori';
            case PataviumRole.SpecializedOperator:
                return 'operatori specializzati';
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/helpers/base-classes/withSearcBarAndSieve.class.ts